import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../../shadcn/ui/accordion";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../../shadcn/ui/tooltip";
import { Mail, MessageSquare } from "lucide-react";
import { useMemo } from "react";

import {
  CHANNEL_PREFERENCE_LABELS,
  SECTION_META,
  USE_CASE_CONFIGS,
  formatChannelDistribution,
} from "../constants";
import { USE_CASE_ICONS_SM } from "../UseCaseCards";
import { CompletionPill } from "../CompletionPill";
import { AccordionSectionId, ChannelPreference, UseCaseType } from "../types";

interface DesktopAccordionProps {
  activeSection: AccordionSectionId | null;
  onSectionChange: (sectionId: string) => void;
  sectionOrder: AccordionSectionId[];
  completedSections: Set<AccordionSectionId>;
  selectedUseCase: UseCaseType | null;
  channelPreference: ChannelPreference | null;
  emailEnabled: boolean;
  smsEnabled: boolean;
  smsCustomized: boolean;
  emailSubject: string;
  audienceVenueDetails: { name: string; fanCount: number }[];
  selectorState: { fanCount: number; emailCount: number; phoneCount: number };
  targeting: { selected: { title: string } | null };
  fansSelected: number;
  renderSectionContent: (sectionId: AccordionSectionId) => React.ReactNode;
}

export function DesktopAccordion({
  activeSection,
  onSectionChange,
  sectionOrder,
  completedSections,
  selectedUseCase,
  channelPreference,
  emailEnabled,
  smsEnabled,
  smsCustomized,
  emailSubject,
  audienceVenueDetails,
  selectorState,
  targeting,
  fansSelected,
  renderSectionContent,
}: DesktopAccordionProps) {
  const selectedConfig = selectedUseCase
    ? USE_CASE_CONFIGS[selectedUseCase]
    : null;

  const isSectionLocked = (sectionId: AccordionSectionId): boolean => {
    const idx = sectionOrder.indexOf(sectionId);
    return (
      idx > 0 &&
      !sectionOrder.slice(0, idx).every((s) => completedSections.has(s))
    );
  };

  const compositionPills = useMemo(() => {
    const truncatedSubject =
      emailSubject.length > 35
        ? emailSubject.slice(0, 35) + "..."
        : emailSubject;

    const smsText = smsCustomized
      ? "Custom"
      : selectedConfig
        ? `${selectedConfig.title} Template`
        : "Template";

    const pills: { key: string; content: React.ReactNode }[] = [];
    if (emailEnabled)
      pills.push({
        key: "email",
        content: (
          <>
            <Mail className="h-3.5 w-3.5" />
            {truncatedSubject || "Email"}
          </>
        ),
      });
    if (smsEnabled)
      pills.push({
        key: "sms",
        content: (
          <>
            <MessageSquare className="h-3.5 w-3.5" />
            {smsText}
          </>
        ),
      });
    if (pills.length === 0) pills.push({ key: "default", content: "Composed" });
    return pills;
  }, [emailSubject, smsCustomized, selectedConfig, emailEnabled, smsEnabled]);

  const renderCompletionPill = (sectionId: AccordionSectionId) => {
    switch (sectionId) {
      case "use-case":
        return (
          completedSections.has("use-case") &&
          activeSection !== "use-case" &&
          selectedUseCase && (
            <CompletionPill>
              {USE_CASE_ICONS_SM[selectedUseCase]}
              {USE_CASE_CONFIGS[selectedUseCase].title}
            </CompletionPill>
          )
        );
      case "audience": {
        if (
          !completedSections.has("audience") ||
          activeSection === "audience" ||
          !targeting.selected
        )
          return null;

        const venueDetails = audienceVenueDetails;
        const fans = fansSelected.toLocaleString();

        if (venueDetails.length === 0) {
          return (
            <CompletionPill>
              {targeting.selected.title} ({fans} fans)
            </CompletionPill>
          );
        }

        const shown = venueDetails.slice(0, venueDetails.length <= 3 ? 3 : 2);
        const remaining = venueDetails.length > 3 ? venueDetails.slice(2) : [];

        return (
          <div className="flex flex-col items-end gap-1">
            {shown.map((v) => (
              <CompletionPill key={v.name}>
                {v.fanCount.toLocaleString()} fans near {v.name}
              </CompletionPill>
            ))}
            {remaining.length > 0 && (
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <CompletionPill>
                        {remaining
                          .reduce((sum, v) => sum + v.fanCount, 0)
                          .toLocaleString()}{" "}
                        fans from {remaining.length} more venue
                        {remaining.length !== 1 ? "s" : ""}
                      </CompletionPill>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent
                    align="end"
                    className="max-w-xs"
                    collisionPadding={10}
                  >
                    <ul className="list-disc pl-4">
                      {remaining.map((v) => (
                        <li key={v.name}>
                          {v.name} ({v.fanCount.toLocaleString()})
                        </li>
                      ))}
                    </ul>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        );
      }
      case "channel-preference":
        return (
          completedSections.has("channel-preference") &&
          activeSection !== "channel-preference" &&
          channelPreference && (
            <CompletionPill>
              {CHANNEL_PREFERENCE_LABELS[channelPreference]}
              {formatChannelDistribution(channelPreference, selectorState)}
            </CompletionPill>
          )
        );
      case "composition":
        return (
          completedSections.has("composition") &&
          activeSection !== "composition" &&
          compositionPills.length > 0 && (
            <div className="flex flex-col items-end gap-1">
              {compositionPills.map((pill) => (
                <CompletionPill key={pill.key}>{pill.content}</CompletionPill>
              ))}
            </div>
          )
        );
      default:
        return null;
    }
  };

  return (
    <Accordion
      type="single"
      collapsible
      value={activeSection || ""}
      onValueChange={onSectionChange}
      className="flex w-full flex-col gap-4"
    >
      {sectionOrder.map((sectionId) => (
        <AccordionItem
          key={sectionId}
          value={sectionId}
          className={`rounded-lg border border-[#C7C8C9] ${isSectionLocked(sectionId) ? "pointer-events-none opacity-50" : ""}`}
          disabled={isSectionLocked(sectionId)}
        >
          <AccordionTrigger className="items-center px-4 py-4 hover:no-underline md:px-6 md:py-5">
            <div className="flex w-full flex-row items-center justify-between gap-x-4">
              <div className="flex flex-1 flex-col items-start gap-1">
                <span
                  className={`text-base font-medium ${activeSection === sectionId ? "text-[#29333C]" : "text-[#787B7E]"}`}
                >
                  {SECTION_META[sectionId].title}
                </span>
              </div>
              {renderCompletionPill(sectionId)}
            </div>
          </AccordionTrigger>
          <AccordionContent className="border-t border-[#C7C8C9] p-6">
            {renderSectionContent(sectionId)}
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}
